import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import db from '@/db';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency } from '@/lib/formatters';
import { Scale, Printer, ChefHat, Bike, UtensilsCrossed, Map, Package, BarChart3, Settings, Receipt, ArrowRight, Users, Activity, Webhook, Bug, MonitorSmartphone } from 'lucide-react';
import { toast } from 'sonner';
import { subscribeSyncState } from '@/lib/firebaseSync';
import NotificationLog from '@/components/NotificationLog';
import AuditLogPanel from '@/components/AuditLogPanel';
import ResetTotalCard from '@/components/ResetTotalCard';
import { useVisiblePolling } from '@/hooks/useVisiblePolling';

const MODULES = [
  { path: '/balanca', label: 'Balança', desc: 'Pesar buffet e abrir comandas', icon: Scale, color: 'bg-emerald-500' },
  { path: '/caixa', label: 'Caixa', desc: 'Fechamento e pagamentos', icon: Receipt, color: 'bg-blue-600' },
  { path: '/cozinha', label: 'Cozinha', desc: 'KDS de preparo', icon: ChefHat, color: 'bg-orange-500' },
  { path: '/delivery-admin', label: 'Delivery', desc: 'Despacho de pedidos', icon: Bike, color: 'bg-purple-500' },
  { path: '/cardapio', label: 'Cardápio', desc: 'CRUD de pratos', icon: UtensilsCrossed, color: 'bg-pink-500' },
  { path: '/logistica', label: 'Logística', desc: 'Raio e taxa por km', icon: Map, color: 'bg-cyan-500' },
  { path: '/estoque', label: 'Estoque', desc: 'Bebidas e itens', icon: Package, color: 'bg-amber-500' },
  { path: '/equipe', label: 'Equipe', desc: 'Gerenciar operadores', icon: Users, color: 'bg-rose-500' },
  { path: '/impressao', label: 'Impressão', desc: 'Testes Jetway', icon: Printer, color: 'bg-slate-500' },
  { path: '/relatorios', label: 'Relatórios', desc: 'Métricas e conversão', icon: BarChart3, color: 'bg-indigo-500' },
  { path: '/admin/zapi-monitor', label: 'Monitor Z-API', desc: 'Webhooks WhatsApp ao vivo', icon: Webhook, color: 'bg-teal-500' },
  { path: '/admin/limpeza-fantasma', label: 'Limpeza Fantasma', desc: 'Caçar pratos fantasma no Firebase', icon: Bug, color: 'bg-rose-600' },
  { path: '/admin/dispositivos', label: 'Dispositivos', desc: 'Sessões ativas e revogação remota', icon: MonitorSmartphone, color: 'bg-cyan-600' },
  { path: '/config', label: 'Configurações', desc: 'Preços e backup', icon: Settings, color: 'bg-slate-600' },
];

export default function AdminHomePage() {
  const navigate = useNavigate();
  const { operator } = useAuth();
  const [stats, setStats] = useState({ openCommands: 0, pendingDeliveries: 0, todayRevenue: 0 });
  const [kpi, setKpi] = useState({ actions: 0, devices: 0 }); // Fase 19 — KPI auditoria
  const [syncToastShown, setSyncToastShown] = useState(false);

  // Fase 14 — Toast "Sistema Online & Sincronizado" quando conecta ao Firebase
  useEffect(() => {
    let wasConnected = false;
    const unsub = subscribeSyncState((s) => {
      if (s.connected && !wasConnected && !syncToastShown) {
        wasConnected = true;
        setSyncToastShown(true);
        toast.success('🔥 Kaven Online: Sincronização Ativa', {
          description: 'Firebase conectado — comandas, cozinha e delivery sincronizam entre todos os dispositivos em tempo real.',
          duration: 4500,
        });
      } else if (!s.connected && wasConnected) {
        wasConnected = false;
      }
    });
    return unsub;
  }, [syncToastShown]);

  const load = async () => {
    // Fase 27 — janela de "hoje" calculada uma vez por chamada
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    const startIso = startOfDay.toISOString();
    // Apenas comandas pagas/abertas relevantes (limit 200 — Caixa típico tem <50)
    const recentCmds = await db.commands.orderBy('createdAt').reverse().limit(200).toArray();
    const recentDelivs = await db.deliveryOrders.orderBy('createdAt').reverse().limit(200).toArray();
    const todayRevenue = recentCmds
      .filter((c) => c.paidAt && new Date(c.paidAt) >= startOfDay && !c.isTraining)
      .reduce((a, c) => a + (c.total || 0), 0)
      + recentDelivs
      .filter((d) => d.status === 'delivered' && new Date(d.createdAt) >= startOfDay && !d.isTraining)
      .reduce((a, d) => a + (d.total || 0), 0);
    const openCmdsReal = recentCmds.filter((c) => c.status === 'open' && !c.archivedAt && !c.isTraining).length;
    const pendingReal = recentDelivs.filter((d) => ['pending', 'preparing', 'ready', 'delivering'].includes(d.status) && !d.isTraining).length;
    setStats({ openCommands: openCmdsReal, pendingDeliveries: pendingReal, todayRevenue });

    // KPI audit do dia (limit 500 já é mais que suficiente)
    try {
      const todayAudit = await db.auditLog.where('at').above(startIso).limit(500).toArray();
      const deviceSet = new Set();
      todayAudit.forEach((a) => {
        const key = a.deviceNickname || a.deviceUid || 'sem-apelido';
        deviceSet.add(key);
      });
      setKpi({ actions: todayAudit.length, devices: deviceSet.size });
    } catch {
      setKpi({ actions: 0, devices: 0 });
    }
  };

  useVisiblePolling(load, 10000);

  return (
    <div data-testid="admin-home" className="p-6 lg:p-8">
      <header className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-400">Painel Administrativo</p>
        <h2 className="font-heading text-3xl tracking-tight font-bold text-slate-900">
          Bem-vindo, {operator?.name || 'Admin'}
        </h2>
        <p data-testid="kpi-audit-line" className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-slate-500">
          <Activity size={12} className="text-emerald-500" strokeWidth={2.25} />
          Hoje: <span data-testid="kpi-actions" className="font-semibold text-slate-700">{kpi.actions}</span> {kpi.actions === 1 ? 'ação' : 'ações'} em{' '}
          <span data-testid="kpi-devices" className="font-semibold text-slate-700">{kpi.devices}</span> {kpi.devices === 1 ? 'dispositivo' : 'dispositivos'}
        </p>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div data-testid="stat-open-commands" className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Receipt size={16} className="text-blue-600" strokeWidth={1.5} />
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Comandas Abertas</p>
          </div>
          <p className="font-mono text-3xl font-black text-slate-900 tracking-tighter">{stats.openCommands}</p>
        </div>
        <div data-testid="stat-pending-deliveries" className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Bike size={16} className="text-purple-600" strokeWidth={1.5} />
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Delivery Pendente</p>
          </div>
          <p className="font-mono text-3xl font-black text-slate-900 tracking-tighter">{stats.pendingDeliveries}</p>
        </div>
        <div data-testid="stat-today-revenue" className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 size={16} className="text-emerald-600" strokeWidth={1.5} />
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Receita de Hoje</p>
          </div>
          <p className="font-mono text-3xl font-black text-slate-900 tracking-tighter">{formatCurrency(stats.todayRevenue)}</p>
        </div>
      </div>

      {/* Modules Grid */}
      <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">Módulos</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {MODULES.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.path}
              data-testid={`admin-module-${m.path.slice(1)}`}
              onClick={() => navigate(m.path)}
              className="group bg-white border border-slate-200 rounded-2xl p-5 text-left hover:border-slate-300 hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl ${m.color} text-white flex items-center justify-center`}>
                  <Icon size={18} strokeWidth={1.75} />
                </div>
                <ArrowRight size={16} strokeWidth={1.5} className="text-slate-300 group-hover:text-slate-600 transition-colors" />
              </div>
              <p className="font-heading font-bold text-slate-900">{m.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{m.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Log de Notificações (Fase 12 - P2) */}
      <div className="mt-8 grid grid-cols-1 xl:grid-cols-2 gap-6">
        <NotificationLog limit={15} />
        <AuditLogPanel limit={15} />
      </div>

      {/* Zona perigosa — Reset Total (Firebase + Local) */}
      <ResetTotalCard />
    </div>
  );
}
